import {Component, inject, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {CardComponent} from "../../../shared-module/components/card/card.component";
import {DatePipe} from "@angular/common";
import {ButtonComponent} from "../../../shared-module/components/button/button.component";
import {MobileService} from "../../../shared-module/services/mobile.service";
import {AuthService} from "../../../shared-module/services/auth.service";
import Relation from "../../../shared-module/model/relation.model";
import Invoice from "../../../shared-module/model/invoice.model";
import ModalService from "../../../shared-module/services/modal.service";
import {InvoiceModalComponent} from "../../../shared-module/components/invoice-modal/invoice-modal.component";
import {DropdownComponent, DropDownItem} from "../../../shared-module/components/dropdown/dropdown.component";
import {Subscription} from "rxjs";
import MonthNamePipe from "../../../shared-module/pipes/month-name.pipe";
import {CurrencyComponent} from "../../../shared-module/components/currency/currency.component";
import {PaymentStatusComponent} from "../../../shared-module/components/payment-status/payment-status.component";
import {BadgeComponent} from "../../../shared-module/components/badge/badge.component";
import {NotFoundComponent} from "../../../shared-module/components/not-found/not-found.component";
import {downloadInvoice} from "../../../shared-module/functions/downloadInvoice.function";

@Component({
    selector: 'app-invoice',
    imports: [
    CardComponent,
    ButtonComponent,
    DatePipe,
    DropdownComponent,
    MonthNamePipe,
    CurrencyComponent,
    PaymentStatusComponent,
    BadgeComponent,
    NotFoundComponent
],
    templateUrl: './invoice.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './invoice.component.scss'
})
export class InvoiceComponent implements OnInit {
  readonly mobileService: MobileService = inject(MobileService);
  private readonly authService: AuthService = inject(AuthService);
  private readonly modalService: ModalService = inject(ModalService);

  public invoices: Invoice[] = [];
  public selectedYear: number = 0;

  private modalSubscription: Subscription | null = null;

  public ngOnInit(): void {
    this.authService.getUser(['relations.invoices.lines']).subscribe();

    this.authService.getActiveRelation().subscribe((relation: Relation | null) => {
      if (!relation) {
        return;
      }

      this.invoices = relation.invoices;

      if (this.getUniqueInvoiceYears.length > 0) {
        this.selectedYear = this.getUniqueInvoiceYears[0].value as number;
      }
    });
  }

  public get getUniqueInvoiceYears(): DropDownItem[] {
    if (!this.invoices) {
      return [];
    }

    return this.invoices
      .map((invoice: Invoice) => new Date(invoice.invoice_date).getFullYear())
      .filter((value: number, index: number, self: number[]) => self.indexOf(value) === index)
      //sort descending
      .sort((a: number, b: number) => b - a)
      .map((y: number) => {
        return {
          label: 'Facturen ' + y,
          value: y,
        } as DropDownItem;
      })
  }

  public showInvoice(invoice: Invoice): void {
    if (this.modalSubscription) {
      return;
    }
    this.modalSubscription = this.modalService.open(InvoiceModalComponent, invoice, {
      width: '900px',
      showCloseButton: true,
      title: 'Factuur: ' + invoice.invoice_number,
      badgeIcon: 'file-lines'
    }).subscribe(() => {
      this.modalSubscription?.unsubscribe();
      this.modalSubscription = null;
    });
  }

  public download(invoice: Invoice): void {
    downloadInvoice(invoice);
  }

  public getInvoicesByYear(selectedYear: number): Invoice[] {
    return this.invoices.filter((invoice: Invoice) => new Date(invoice.invoice_date).getFullYear() === selectedYear);
  }

  public getInvoicesByYearAndMonth(selectedMonth: number): Invoice[] {
    const invoices: Invoice[] = this.invoices.filter((invoice: Invoice) => {
      return new Date(invoice.invoice_date).getFullYear() === this.selectedYear && new Date(invoice.invoice_date).getMonth() === selectedMonth;
    });

    // Sort so the latest invoice is at the top.
    return invoices.sort(function (a: Invoice, b: Invoice) {
      if (a.invoice_number > b.invoice_number) {
        return -1;
      }

      if (a.invoice_number < b.invoice_number) {
        return 1;
      }

      return 0;
    });
  }

  public getMonthsOfSelectedYear(): number[] {
    const invoices: Invoice[] = this.getInvoicesByYear(this.selectedYear);

    const months: number[] = invoices.map((invoice: Invoice) => {
      return new Date(invoice.invoice_date).getMonth();
    });

    // Keep unique months and sort by latest.
    return [...new Set(months)].sort((a: number, b: number) => b - a);
  }
}
